import apiClient from './client';

export interface InsightFilters {
  accountId: string;
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}

export interface Insight {
  id: string;
  name: string;
  spend: number;
  impressions: number;
  clicks: number;
  reach: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cpl: number | null;
  dealsWon: number;
  revenue: number;
  costPerDeal: number | null;
  roas: number | null;
  status?: string;
  adIds?: string[];
}

export interface InsightsSummary {
  totalSpend: number;
  totalLeads: number;
  totalDeals: number;
  totalRevenue: number;
  totalImpressions: number;
  totalClicks: number;
  totalReach: number;
  avgCpl: number | null;
  avgCostPerDeal: number | null;
  avgRoas: number | null;
  avgCtr: number;
  avgCpc: number;
  avgCpm: number;
}

export interface DailyTrend {
  date: string;
  totalSpend: number;
  totalLeads: number;
  totalDeals: number;
  totalRevenue: number;
}

export async function getInsights(filters: InsightFilters): Promise<Insight[]> {
  const params = new URLSearchParams();
  params.append('accountId', filters.accountId);
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await apiClient.get(`/insights?${params.toString()}`);
  return response.data.insights;
}

export async function getInsightsSummary(
  filters: InsightFilters
): Promise<InsightsSummary> {
  const params = new URLSearchParams();
  params.append('accountId', filters.accountId);
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await apiClient.get(`/insights/summary?${params.toString()}`);
  return response.data.summary;
}

export async function getInsightsTrends(
  filters: InsightFilters
): Promise<{ daily: DailyTrend[] }> {
  const params = new URLSearchParams();
  params.append('accountId', filters.accountId);
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await apiClient.get(`/insights/trends?${params.toString()}`);
  return response.data.trends;
}

// Website / "אתר" — Zoho-sourced lead stats (no ad backs them).
export interface WebsiteStats {
  totalLeads: number;
  closedDeals: number;
  totalRevenue: number;
}

export async function getWebsiteStats(filters: {
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}): Promise<WebsiteStats> {
  const params = new URLSearchParams();
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await apiClient.get(`/insights/website-stats?${params.toString()}`);
  return response.data.stats;
}

// Manual DM / WhatsApp leads — same shape as WebsiteStats.
export async function getDmStats(filters: {
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}): Promise<WebsiteStats> {
  const params = new URLSearchParams();
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await apiClient.get(`/insights/dm-stats?${params.toString()}`);
  return response.data.stats;
}

// Dashboard snapshot — single payload covering the last N days (default 60)
// of platform-daily + Zoho deal data. The dashboard consumes this once and
// computes every in-window KPI client-side, so account / date-range changes
// inside the window don't trigger network calls.
export interface SnapshotDailyRow {
  date: string; // YYYY-MM-DD (Israel timezone bucket)
  spend: number;
  impressions: number;
  clicks: number;
  reach: number;
}

export interface SnapshotAccount {
  _id: string;
  platform: string;
  platformAccountId: string;
  name: string;
  leadSource?: string;
  daily: SnapshotDailyRow[];
}

export interface SnapshotDeal {
  id: string;
  createdTime: string; // ISO
  leadSource: string;
  stage: string;
  amount: number;
  formName: string | null;
}

export interface DashboardSnapshot {
  windowStart: string; // YYYY-MM-DD
  windowEnd: string;
  fetchedAt: string;
  days: number;
  accounts: SnapshotAccount[];
  zohoDeals: SnapshotDeal[];
}

export async function getSnapshot(
  days: number = 60,
  refresh: boolean = false
): Promise<DashboardSnapshot> {
  const params = new URLSearchParams();
  params.append('days', String(days));
  if (refresh) params.append('refresh', 'true');

  const response = await apiClient.get(`/insights/snapshot?${params.toString()}`);
  return response.data.snapshot;
}


export interface JobPromotionsSummary {
  jobsCount: number;
  totalCandidates: number;
  totalSpend: number;
  avgCostPerCandidate: number | null;
  last24hCandidates: number;
}

export async function getJobPromotionsSummary(
  startDate: string,
  endDate: string
): Promise<JobPromotionsSummary> {
  const response = await apiClient.get('/insights/job-promotions', {
    params: { dateRange: 'custom', startDate, endDate },
  });
  return response.data.summary;
}
